import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { ButtonProps, CircularProgress, Container, IconButton, InputAdornment, Paper, Stack, TextField, useTheme } from "@mui/material";
import { GridPaginationModel, GridRowParams } from "@mui/x-data-grid";
import { DataGrid } from "@/components";
import PageHeader from "@/components/PageHeader";
import StatusFilters from "./StatusFilters";
import ContentDrawer from "./Drawer";
import { useLazyFetchContentByStatusQuery } from "@/features/organizations/api/contentApi";
import { useAccessControl } from "@/features/access-control";
import { useAppDispatch, useAppSelector, useTerminology } from "@/hooks";
import OfferDrawer from "./offers/OfferDrawer";
import { useContentColumns } from "./hooks/useContentColumns";
import ButtonGroup from "@/components/ButtonGroup";
import { Content, ContentStatus, Offer } from "@/types";
import routes from "@/constants/routes";
import AvailableQuota from "@/components/AvailableQuota";
import useOrganizationPlan from "@/hooks/useOrganizationPlan";
import { HugeiconsIcon } from "@hugeicons/react";
import { Search01Icon, Cancel01Icon } from "@hugeicons/core-free-icons";
import { setContainer } from "@/store/uiSlice";

const { DRAFT, PUBLISHED, ARCHIVED } = ContentStatus;

const PAGE_SIZE = 10;

const STATUS_MAP: Record<string, ContentStatus> = {
  publicados: PUBLISHED,
  borradores: DRAFT,
  archivados: ARCHIVED,
};
type Params = {
  organizationId: string;
  contentStatus: "publicados" | "borradores" | "archivados";
  contentId?: string;
};

const OrganizationContent = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const container = useAppSelector((state) => state.ui?.container);
  const { t } = useTerminology();
  const { organizationId = "", contentStatus = "publicados", contentId } = useParams<Params>();
  const [activeDrawer, setActiveDrawer] = useState<"dish" | "offer" | null>(null);
  const { hasAccess } = useAccessControl();
  const { checkFeatureLimit, openUpgradePopUp } = useOrganizationPlan("content");
  const users = useAppSelector((state) => state.users);
  const [searchQuery, setSearchQuery] = useState("");
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: PAGE_SIZE });
  const globalCategories = useAppSelector((state) => Object.values(state.categories ?? {})) ?? [];
  const organizationCategories = useAppSelector((state) => state.organizations[organizationId ?? ""]?.categories) ?? [];
  const categories = [...globalCategories, ...organizationCategories];
  const columns = useContentColumns({ categories });

  const [fetchContent, { data, isLoading }] = useLazyFetchContentByStatusQuery();

  const handleFetch = useCallback((action: "none" | "next" | "previous", currentData?: any, search?: string) => {
    if (!organizationId) return;

    fetchContent({
      organizationId,
      status: STATUS_MAP[contentStatus],
      pageSize: PAGE_SIZE,
      action,
      firstDocId: currentData?.firstDocId,
      lastDocId: currentData?.lastDocId,
      searchQuery: search || undefined,
    } as any);
  }, [organizationId, contentStatus, fetchContent]);

  const isInitialMount = useRef(true);

  // Initial fetch + search debounce (same pattern as OrdersGrid)
  useEffect(() => {
    if (isInitialMount.current) {
      handleFetch("none", undefined, searchQuery);
      isInitialMount.current = false;
      return;
    }

    const handler = setTimeout(() => {
      handleFetch("none", undefined, searchQuery);
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery, handleFetch]);

  // Re-fetch when status changes
  useEffect(() => {
    if (!isInitialMount.current) {
      handleFetch("none", undefined, searchQuery);
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    }
  }, [contentStatus, organizationId]);

  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    const action = newModel.page > paginationModel.page ? "next" : "previous";
    handleFetch(action, data, searchQuery);
    setPaginationModel(newModel);
  };

  const contentItems = useMemo(() => data?.items ?? [], [data]);
  const totalCount = data?.totalCount ?? 0;
  const globalCount = data?.globalCount ?? 0;

  const selectedContent = useMemo(() => {
    if (!contentId || !contentItems.length) return undefined;
    return contentItems.find(({ id }: { id: string }) => id === contentId);
  }, [contentId, contentItems]);

  const enrichedContent = useMemo(() => {
    return contentItems.map((content: Content) => {
      const user = users[content.createdBy];
      return {
        ...content,
        createdBy: user ? user.name : "",
      };
    });
  }, [contentItems, users]);

  const actions = useMemo(() => {
    const draft: ButtonProps[] = [];

    if (!hasAccess("create.content")) {
      return draft;
    }

    draft.push({
      children: `Crear ${t("product", 1, true)}`,
      color: "success",
      onClick: () => {
        if (checkFeatureLimit(globalCount)) {
          setActiveDrawer("dish");
        } else {
          openUpgradePopUp();
        }
      },
    });

    draft.push({
      children: "Crear Oferta",
      color: "success",
      onClick: () => setActiveDrawer("offer"),
    });

    return draft;
  }, [hasAccess, checkFeatureLimit, globalCount, openUpgradePopUp, t]);

  const closeDrawer = () => {
    setActiveDrawer(null);
    navigate(
      generatePath(routes.organizations.content.main, {
        organizationId,
        contentStatus,
      }),
    );
  };

  useEffect(() => {
    dispatch(setContainer("fluid"));
    return () => {
      dispatch(setContainer("fixed"));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Actions = () => {
    if (hasAccess("create.content")) {
      return (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center" alignItems="center">
          <AvailableQuota feature="content" usedQuota={globalCount} />
          <ButtonGroup actions={actions} fullWidth />
        </Stack>
      );
    }

    return null;
  };

  if (isLoading && !data) {
    return (
      <Stack flex={1} alignContent="center" justifyContent="center" padding={3}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack spacing={2} pb={3}>
      <PageHeader title="Contenido" action={<Actions />} />
      <Container
        maxWidth={container === 'fixed' ? 'lg' : false}
        sx={{
          height: '100%',
        }}
      >
        <Stack spacing={2} height={'100%'}>
          <Paper>
            <Stack gap={{ xs: 2, sm: 3 }} p={2} direction={{ sm: "row" }} flex={1} alignItems="center" justifyContent="space-between">
              <TextField
                placeholder="Buscar por título"
                size="small"
                fullWidth
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <HugeiconsIcon icon={Search01Icon} size={20} color={theme.palette.primary.main} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery("")}>
                        <HugeiconsIcon icon={Cancel01Icon} size={20} color={theme.palette.error.main} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
              <StatusFilters />
            </Stack>
          </Paper>
          <Paper>
            <DataGrid
              hideFooter={false}
              columns={columns}
              rows={enrichedContent}
              paginationMode="server"
              rowCount={totalCount}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              loading={isLoading}
              onRowClick={(params: GridRowParams<Content | Offer>) => {
                setActiveDrawer(params.row.type ? params.row.type : "dish");
                navigate(
                  generatePath(routes.organizations.content.details, {
                    organizationId,
                    contentStatus,
                    contentId: params.row.id,
                  }),
                );
              }}
              density="comfortable"
              sx={{
                border: "none",
                marginBottom: 0,
                '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': {
                  paddingLeft: 2,
                  paddingRight: 2,
                },
                '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-columnHeader': {
                  paddingLeft: 2,
                  paddingRight: 2,
                }
              }}
            />
          </Paper>
          <ContentDrawer open={activeDrawer === "dish" || selectedContent?.type === "dish"} selectedContent={selectedContent as any} onClose={closeDrawer} />
          <OfferDrawer open={activeDrawer === "offer" || selectedContent?.type === "offer"} selectedContent={selectedContent as any} onClose={closeDrawer} />
        </Stack>
      </Container>
    </Stack>
  );
};

export default OrganizationContent;
